import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../functions/api'; // DAS-3127: sends the login token (raw axios got 401)
import { useSelector } from 'react-redux';
import InfoTooltip from './InfoTooltip';

const getTrendColor = (trendDirection) => {
  if (typeof trendDirection !== 'string') return 'default';
  const trend = trendDirection.toLowerCase();
  if (trend.includes('bullish')) return 'success';
  if (trend.includes('bearish')) return 'error';
  if (trend.includes('neutral')) return 'default';
  return 'default';
};

const mapInstitutionalSignalToScore = (signal) => {
  if (!signal || typeof signal !== 'string') return 0;
  const s = signal.toLowerCase();
  if (s.includes('strong buy')) return 2;
  if (s.includes('weak buy')) return 1;
  if (s.includes('neutral')) return 0;
  if (s.includes('weak sell')) return -1;
  if (s.includes('strong sell')) return -2;
  return 0;
};

const scoreToInstitutionalLabel = (avg) => {
  if (avg >= 1.5) return 'Strong Buy';
  if (avg >= 0.5) return 'Weak Buy';
  if (avg <= -1.5) return 'Strong Sell';
  if (avg <= -0.5) return 'Weak Sell';
  return 'Neutral';
};

const labelToChipColor = (label) => {
  switch (label) {
    case 'Strong Buy':
      return 'success';
    case 'Weak Buy':
      return 'success';
    case 'Neutral':
      return 'default';
    case 'Weak Sell':
      return 'warning';
    case 'Strong Sell':
      return 'error';
    default:
      return 'default';
  }
};

const SectorOverview = ({ open, onClose, inline = false }) => {
  // Redux data
  const sectorTrend = useSelector(state => state.portfolio?.data?.Sector_trend || {});
  const sectorTrendPrev = useSelector(state => state.portfolio?.data?.Sector_trend_previous || {});
  const institutionalTrends = useSelector(state => state.portfolio?.data?.Vector_institutional_trend || {});
  const marketPositioningData = useSelector(state => state.portfolio?.data?.Market_Positioning_Analysis || {});

  // Trendsearch sector summary
  const [sectorSummary, setSectorSummary] = useState([]);
  const [sectorIndustrySummary, setSectorIndustrySummary] = useState([]);
  const [midRegionSectorRows, setMidRegionSectorRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const shouldFetch = inline || open;
    if (!shouldFetch) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [res1, res2, res3] = await Promise.allSettled([
          api.get('/trendsearch_sector_summary'),
          api.get('/trendsearch_sector_industry_summary'),
          api.get('/institutional_region_sector')
        ]);

        if (!cancelled && res1.status === 'fulfilled') {
          const d = res1.value?.data;
          if (d?.success) setSectorSummary(d.data || []);
        }
        if (!cancelled && res2.status === 'fulfilled') {
          const d = res2.value?.data;
          if (d?.success) setSectorIndustrySummary(d.data || []);
        }
        if (!cancelled && res3.status === 'fulfilled') {
          const d = res3.value?.data;
          if (d?.success) setMidRegionSectorRows(d.data || []);
        }
      } catch (e) {
        // ignore
      } finally {
        !cancelled && setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, inline]);

  const regionList = useMemo(() => {
    const cols = (institutionalTrends?.columns || []).slice(2);
    const regions = Array.from(new Set(cols.map(col => {
      const name = String(col).replace(/^Institutional_trend_/, '');
      const parts = name.split('_');
      return parts[0];
    })));
    const desired = ['Global', 'USA', 'Europe', 'Japan', 'Taiwan', 'China'];
    const inDesired = desired.filter(r => regions.includes(r));
    const others = regions.filter(r => !desired.includes(r)).sort();
    return [...inDesired, ...others];
  }, [institutionalTrends]);

  const regionShort = {
    Global: 'Global',
    USA: 'US',
    Europe: 'EU',
    Japan: 'JP',
    Taiwan: 'TW',
    China: 'CN'
  };

  const midColorFor = (value) => {
    if (!value) return 'default';
    if (value.includes('Strong Buying')) return 'success';
    if (value.includes('Weak Buying')) return 'success';
    if (value.includes('Weak Selling')) return 'warning';
    if (value.includes('Strong Selling')) return 'error';
    return 'default';
  };

  const SectorAlias = { 'Consumer Discretionary':'Consumer Cyclical', 'Consumer Staples':'Consumer Defensive' };
  // Important: keep regions in their long form to match Vector_institutional_trend columns (e.g., 'USA', 'Europe', 'China').
  const canonicalRegion = (r) => r;
  const canonicalSector = (s) => SectorAlias[s] || s;

  // Convert possible SQL region codes to long names used in UI
  const toLongRegion = (r) => {
    const m = {
      'US': 'USA', 'USA': 'USA', 'United States': 'USA',
      'EU': 'Europe', 'Europe': 'Europe',
      'CN': 'China', 'China': 'China',
      'JP': 'Japan', 'Japan': 'Japan',
      'TW': 'Taiwan', 'Taiwan': 'Taiwan',
      'Global': 'Global'
    };
    return m[String(r)] || String(r);
  };

  const sectorRows = useMemo(() => {
    const { columns = [], rows = [] } = sectorTrend;
    if (!columns.length || !rows.length) return [];

    // Build institutional short-term consensus per sector
    const instCols = (institutionalTrends?.columns || []).slice(2) || [];
    const instRow = (institutionalTrends?.rows || [])[0] || [];

    // Parse mid-term JSON arrays (fallback)
    let top5Strong = [];
    let top5Weak = [];
    let regionSectorAll = [];
    try {
      // Prefer normalized SQL rows if available
      if (Array.isArray(midRegionSectorRows) && midRegionSectorRows.length > 0) {
        regionSectorAll = midRegionSectorRows.map(r => ({
          region: toLongRegion(r.region),
          sector: canonicalSector(r.sector),
          pick: r.pick
        }));
      }

      if (marketPositioningData?.rows && marketPositioningData?.rows[0] && regionSectorAll.length === 0) {
        const mj = marketPositioningData.rows[0][marketPositioningData.columns.indexOf('full_result_json')];
        const json = typeof mj === 'string' ? JSON.parse(mj) : mj;
        top5Strong = json?.top5_strong || [];
        top5Weak = json?.top5_weak || [];
        if (json?.region_sector_map) {
          for (const [region, sectorMap] of Object.entries(json.region_sector_map)) {
            for (const [sector, pick] of Object.entries(sectorMap)) {
              regionSectorAll.push({ region: canonicalRegion(region), sector: canonicalSector(sector), pick });
            }
          }
        }
        const resultHtml = marketPositioningData.rows[0][marketPositioningData.columns.indexOf('result')];
        if (regionSectorAll.length === 0 && typeof resultHtml === 'string' && resultHtml.includes('<table')) {
          const rows = [...resultHtml.matchAll(/<tr><td>([^<]+)<\/td><td>([^<]+)<\/td><td>(?:<span[^>]*>)?([^<]+)(?:<\/span>)?<\/td><\/tr>/g)];
          regionSectorAll = rows.map(m => ({ region: m[1], sector: canonicalSector(m[2]), pick: m[3] }));
        }
      }
    } catch (_) {}

    const normalize = (s) => String(s || '').toLowerCase().replace(/_/g, ' ').trim();

    return rows.map((r) => {
      const sectorName = r[2];
      const trendDirection = r[4];

      // Institutional short-term by region for this sector
      const instShortByRegion = {};
      regionList.forEach(region => {
        let signal = null;
        for (let idx = 0; idx < instCols.length; idx++) {
          const col = instCols[idx];
          const name = String(col).replace(/^Institutional_trend_/, '');
          const parts = name.split('_');
          const colRegion = parts[0];
          const sectorPart = parts.slice(1).join(' ');
          if (colRegion === region && normalize(sectorPart) === normalize(sectorName)) {
            signal = instRow[idx + 2];
            break;
          }
        }
        if (typeof signal === 'string' && signal.length > 0) {
          instShortByRegion[region] = signal;
        }
      });

      // Mid-term by region for this sector (prefer normalized SQL rows)
      const instMidByRegion = {};
      if (regionSectorAll.length > 0) {
        regionList.forEach(region => {
          const hit = regionSectorAll.find(item => normalize(item.region) === normalize(region) && normalize(item.sector) === normalize(sectorName));
          if (hit && hit.pick) instMidByRegion[region] = hit.pick;
        });
      } else {
        regionList.forEach(region => {
          const strongHit = top5Strong.find(item => normalize(item?.region) === normalize(region) && normalize(item?.sector) === normalize(sectorName));
          const weakHit = top5Weak.find(item => normalize(item?.region) === normalize(region) && normalize(item?.sector) === normalize(sectorName));
          if (strongHit || weakHit) {
            let label = 'Neutral';
            if (strongHit && !weakHit) label = 'Strong Buying';
            else if (!strongHit && weakHit) label = 'Strong Selling';
            else if (strongHit && weakHit) label = 'Mixed';
            instMidByRegion[region] = label;
          }
        });
      }

      return {
        sector: sectorName,
        trend: trendDirection,
        trendChangeSymbol: (sectorTrendPrev[sectorName] && sectorTrendPrev[sectorName].trend_direction !== trendDirection)
          ? (trendDirection?.toLowerCase().includes('bullish') ? '↗' : trendDirection?.toLowerCase().includes('bearish') ? '↘' : '↔')
          : null,
        instShortByRegion,
        instMidByRegion,
        tsSentiment: (sectorSummary.find(s => (s.sector || '').toLowerCase() === String(sectorName).toLowerCase()) || {}).overall_sentiment ?? null,
        tsStatus: (sectorSummary.find(s => (s.sector || '').toLowerCase() === String(sectorName).toLowerCase()) || {}).sentiment_status || 'N/A',
        industries: sectorIndustrySummary
          .filter(i => (i.sector || '').toLowerCase() === String(sectorName).toLowerCase())
          .sort((a, b) => Math.abs((b.overall_sentiment ?? 0)) - Math.abs((a.overall_sentiment ?? 0)))
          .slice(0, 6)
      };
    });
  }, [sectorTrend, institutionalTrends, marketPositioningData, sectorSummary, sectorTrendPrev, regionList, sectorIndustrySummary, midRegionSectorRows]);

  const content = (
    <>
      {!inline && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">📚 Sector Overview</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      <TableContainer component={Paper} sx={{ maxHeight: inline ? 'none' : '70vh' }}>
        <Table stickyHeader={!inline} size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Sector
                  <InfoTooltip text="Financial sector classification (e.g., Technology, Healthcare)." placement="top" />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  30d Weighted Trend
                  <InfoTooltip text="Market-cap weighted 30-day trend of sector constituents." placement="top" />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Institutional (Short) by Region
                  <InfoTooltip text="Short-term institutional flow signal per region (ETF A/D Line, OBV)." placement="top" />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Institutional (Mid) by Region
                  <InfoTooltip text="Mid-term institutional positioning per region from aggregated ETF flows." placement="top" />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Trendsearch Sentiment
                  <InfoTooltip text="Aggregated sector-level sentiment; cell background shows overall; chips show top industries." placement="top" />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sectorRows.map((row, idx) => (
              <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.sector}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip size="small" label={row.trend} color={getTrendColor(row.trend)} />
                    {row.trendChangeSymbol && (
                      <Typography variant="caption" sx={{ color: '#666' }}>{row.trendChangeSymbol}</Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {Object.keys(row.instShortByRegion).map((region) => (
                      <Tooltip key={region} title={`${region}: ${row.instShortByRegion[region]}`}>
                        <Chip size="small" label={`${regionShort[region] || region}`} color={labelToChipColor(row.instShortByRegion[region])} />
                      </Tooltip>
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {Object.keys(row.instMidByRegion).map((region) => (
                      <Tooltip key={region} title={`${region}: ${row.instMidByRegion[region]}`}>
                        <Chip size="small" label={`${regionShort[region] || region}`} color={midColorFor(row.instMidByRegion[region])} />
                      </Tooltip>
                    ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor:
                      row.tsSentiment == null
                        ? 'transparent'
                        : row.tsSentiment >= 3
                        ? '#E8F5E9'
                        : row.tsSentiment <= -3
                        ? '#FFEBEE'
                        : '#F5F5F5'
                  }}
                >
                  <Tooltip title={`Overall sentiment: ${row.tsSentiment ?? 'N/A'} (${row.tsStatus})`}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {row.industries.map((ind, idx) => (
                          <Tooltip key={idx} title={`${ind.industry}: ${ind.overall_sentiment} (${ind.sentiment_status})`}>
                            <Chip size="small" label={ind.industry} color={ind.overall_sentiment >= 3 ? 'success' : ind.overall_sentiment <= -3 ? 'error' : 'default'} />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          Notes: 30d trend from market-cap weighted stock trends; Institutional (Short) aggregated across regions; Institutional (Mid) from mid-term positioning; Trendsearch sentiment from aggregated sector-level changes.
        </Typography>
      </Box>
    </>
  );

  if (inline) {
    return (
      <Box sx={{ p: 1 }}>{content}</Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">📚 Sector Overview</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>{content}</DialogContent>
    </Dialog>
  );
};

export default SectorOverview;


